import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Tabs, Tab, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useModalBackHandler } from '../shared/modalBackHandler';
import { getSmartRedirectPath } from '../shared/accountCache';

export default function AuthModal({ show, handleClose, redirectPath }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { login, register, googleLogin, error, setError } = useAuth();

  // Use our custom hook to handle browser back button (without warning since no data to lose)
  useModalBackHandler(show, handleClose, false);
  
  // Conditional console.log (controlled by admin via localStorage)
  // Admins can enable: localStorage.setItem('trip147_debug_auth', 'true')
  const clog = (...args) => {
    if (localStorage.getItem('trip147_debug_auth') === 'true') {
      console.log(...args);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setError(''); // Clear any errors when switching tabs
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const success = await googleLogin(credentialResponse.credential);
      
      if (success) {
        handleClose();
        
        // Get fresh user data from localStorage
        const userData = JSON.parse(localStorage.getItem('user'));
        
        // Use provided redirect path, or determine smart redirect path
        // IMPORTANT: forceRefresh=false because syncTourStatusFromAccount() already refreshed account data in googleLogin()
        // This prevents duplicate server calls - account cache is already fresh!
        const finalRedirectPath = redirectPath 
          ? decodeURIComponent(redirectPath)
          : await getSmartRedirectPath(userData, false);
        clog('[AuthModal - Google] Redirecting to:', finalRedirectPath);
        navigate(finalRedirectPath);
      }
    } catch (error) {
      console.error('Google auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(t('auth.googleSignInFailed'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let success;
      
      if (activeTab === 'login') {
        success = await login(email, password);
      } else {
        success = await register(name, email, password);
        if (success) {
          // If registration is successful, automatically log in
          success = await login(email, password);
        }
      }
      
      if (success) {
        handleClose();
        // Reset form
        setEmail('');
        setPassword('');
        setName('');
        
        // Get fresh user data from localStorage (state hasn't updated yet)
        const userData = JSON.parse(localStorage.getItem('user'));
        
        // Use provided redirect path, or determine smart redirect path
        // IMPORTANT: forceRefresh=false because syncTourStatusFromAccount() already refreshed account data in login()
        // This prevents duplicate server calls - account cache is already fresh!
        const finalRedirectPath = redirectPath 
          ? decodeURIComponent(redirectPath)
          : await getSmartRedirectPath(userData, false);
        clog('[AuthModal - Login/Register] Redirecting to:', finalRedirectPath);
        navigate(finalRedirectPath);
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {activeTab === 'login' ? t('auth.login') : t('auth.signUp')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={handleTabChange}
          className="mb-3"
        >
          <Tab eventKey="login" title={t('auth.login')}>
            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form.Group className="mb-3" controlId="loginEmail">
                <Form.Label>{t('auth.emailAddress')}</Form.Label>
                <Form.Control 
                  type="email" 
                  placeholder={t('auth.enterEmail')} 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="loginPassword">
                <Form.Label>{t('auth.password')}</Form.Label>
                <Form.Control 
                  type="password" 
                  placeholder={t('auth.password')} 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="text-end mt-2">
                  <Button 
                    variant="link" 
                    className="p-0 text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                      navigate('/forgot-password');
                    }}
                  >
                    {t('auth.forgotPassword')}
                  </Button>
                </div>
              </Form.Group>
              
              <div className="d-grid gap-2">
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t('auth.loggingIn')}
                    </>
                  ) : t('auth.login')}
                </Button>
              </div>

              <div className="text-center my-3">
                <small className="text-muted">{t('auth.or')}</small>
              </div>

              <div className="d-flex justify-content-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text="signin_with"
                  shape="rectangular"
                  size="large"
                  width="300"
                  locale="en"
                />
              </div>
            </Form>
          </Tab>
          
          <Tab eventKey="signup" title={t('auth.signUp')}>
            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form.Group className="mb-3" controlId="signupName">
                <Form.Label>{t('auth.fullName')}</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder={t('auth.enterYourName')} 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="signupEmail">
                <Form.Label>{t('auth.emailAddress')}</Form.Label>
                <Form.Control 
                  type="email" 
                  placeholder={t('auth.enterEmail')} 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="signupPassword">
                <Form.Label>{t('auth.password')}</Form.Label>
                <Form.Control 
                  type="password" 
                  placeholder={t('auth.password')} 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Form.Group>
              
              <div className="d-grid gap-2">
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t('auth.creatingAccount')}
                    </>
                  ) : t('auth.signUp')}
                </Button>
              </div>

              <div className="text-center my-3">
                <small className="text-muted">{t('auth.or')}</small>
              </div>

              <div className="d-flex justify-content-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text="signup_with"
                  shape="rectangular"
                  size="large"
                  width="300"
                  locale="en"
                />
              </div>
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
} 